import React, { useState, useEffect } from "react";
import { InfiniteScroll } from "@/components/InfiniteScroll";
import { useNavigate } from "react-router-dom";
import { useLoading } from "@/lib/LoadingHook";
import { AdminPageLayout } from "../../../layouts/AdminPageLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormError } from "@/components/FormError";
import {
  getPackages,
  createPackage,
  deletePackage,
} from "@/services/admin/packages.service";
import {
  Package,
  CreatePackageInputs,
} from "@/services/admin/packages.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { useRefresher } from "@/components/grids/DataGridComponent";
import { getImageUrl } from "@/lib/media";

const PACKAGE_COVER_PLACEHOLDER =
  "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop";

const packageSchema = yup.object().shape({
  name: yup
    .string()
    .required("Nome do pacote é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres"),
  description: yup
    .string()
    .transform((value) => (!value?.trim() ? undefined : value))
    .optional()
    .test(
      "min",
      "Descrição deve ter pelo menos 10 caracteres quando fornecida",
      (value) => !value || value.length >= 10
    ),
});

interface ToolbarProps {
  onCreateClick: () => void;
}

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePackageInputs) => void;
  isLoading?: boolean;
}

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ onCreateClick }) => (
  <section id="tools" className="mb-6">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex flex-1 items-center space-x-4">
        <div className="relative flex-1 max-w-xl">
          <Input
            type="search"
            placeholder="Search packages..."
            className="pl-10"
          />
          <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>
        <button className="flex items-center space-x-2 rounded-lg border border-neutral-300 px-4 py-2">
          <i className="fa-solid fa-filter text-neutral-600"></i>
          <span>Filters</span>
        </button>
      </div>
      <button
        onClick={onCreateClick}
        className="flex items-center space-x-2 rounded-lg bg-neutral-900 px-4 py-2 text-white"
      >
        <i className="fa-solid fa-plus"></i>
        <span>Create Package</span>
      </button>
    </div>
  </section>
);

interface PackageCardProps {
  pkg: Package;
  onDeleteClick: () => void;
}

const PackageCard: React.FC<PackageCardProps> = ({ pkg, onDeleteClick }) => {
  const navigate = useNavigate();
  const coverSrc =
    getImageUrl(pkg.imageUrl || pkg.image) || PACKAGE_COVER_PLACEHOLDER;
  const openPackage = () => navigate(`/admin/packages/${pkg.id}`);
  const onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPackage();
    }
  };
  const onDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDeleteClick();
  };

  return (
    <div
      className="cursor-pointer overflow-hidden rounded-lg border border-border bg-card shadow-sm hover:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      role="link"
      tabIndex={0}
      aria-label={`Edit ${pkg.name}`}
      onClick={openPackage}
      onKeyDown={onCardKeyDown}
    >
      <div className="relative h-[8.275rem] overflow-hidden">
        <img src={coverSrc} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-foreground/45" />
        {pkg.level ? (
          <span className="absolute bottom-2.5 left-3 rounded-sm bg-foreground/50 px-1.5 py-0.5 text-xs text-primary-foreground">
            {pkg.level}
          </span>
        ) : null}
      </div>
      <div className="px-4 pb-4 pt-3.5">
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold tracking-tight text-foreground">
            {pkg.name}
          </h3>
          <button
            type="button"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center align-middle rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive transition-all active:scale-95"
            aria-label={`Delete ${pkg.name}`}
            onClick={onDelete}
          >
            <i className="fa-solid fa-trash text-xs leading-none" aria-hidden="true" />
          </button>
        </div>
        <p className="mb-2 line-clamp-2 text-xs leading-snug text-muted-foreground">
          {pkg.description}
        </p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <i className="fa-solid fa-book" aria-hidden="true"></i>
          <span>{pkg.modulesCount} Modules</span>
        </div>
      </div>
    </div>
  );
};

const CreateModal: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreatePackageInputs>({
    resolver: yupResolver(packageSchema),
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${
        isOpen ? "" : "hidden"
      }`}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-lg bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Create New Package</h2>
            <button
              onClick={handleClose}
              className="rounded-full p-2 hover:bg-neutral-100"
            >
              <i className="fa-solid fa-times text-neutral-600"></i>
            </button>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="name">Package Name</Label>
              <Input id="name" {...register("name")} />
              <FormError message={errors.name?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={3}
                {...register("description")}
              />
              <FormError message={errors.description?.message} />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Save Package
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteModal: React.FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => (
  <div
    className={`fixed inset-0 bg-black bg-opacity-50 ${isOpen ? "" : "hidden"}`}
  >
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6">
        <div className="mb-6 text-center">
          <i className="fa-solid fa-exclamation-triangle text-4xl text-neutral-600 mb-4"></i>
          <h2 className="text-xl font-semibold">Delete Package?</h2>
          <p className="mt-2 text-neutral-600">
            Are you sure you want to delete this package? This action cannot be
            undone.
          </p>
        </div>
        <div className="flex justify-center space-x-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-300 px-4 py-2"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-neutral-900 px-4 py-2 text-white"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
);

export const AdminPackagesPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  );
  const { isLoading, whenLoading } = useLoading();
  const toast = useAppToast();

  const refreshHandle = useRefresher();

  const fetchPackages = async (page: number) => {
    try {
      return await getPackages(page);
    } catch (error) {
      toast.error("Failed to load packages");
      console.error("Error loading packages:", error);
      return [];
    }
  };

  const renderItem = (pkg: Package, key: React.Key) => (
    <PackageCard
      key={key}
      pkg={pkg}
      onDeleteClick={() => {
        setSelectedPackageId(pkg.id);
        setIsDeleteModalOpen(true);
      }}
    />
  );

  const itemKey = (pkg: Package) => pkg.id;

  const handleCreatePackage = async (data: CreatePackageInputs) => {
    const success = await whenLoading(async () => {
      try {
        await createPackage(data);
        toast.success("Package created successfully");
        refreshHandle.refresh();
        return true;
      } catch (error) {
        toast.error("Failed to create package");
        console.error("Error creating package:", error);
        return false;
      }
    });

    if (success) {
      setIsCreateModalOpen(false);
    }
  };

  const handleDeletePackage = async () => {
    if (!selectedPackageId) return;

    const success = await whenLoading(async () => {
      try {
        await deletePackage([selectedPackageId]);
        toast.success("Package deleted successfully");
        refreshHandle.refresh();
        return true;
      } catch (error) {
        toast.error("Failed to delete package");
        console.error("Error deleting package:", error);
        return false;
      }
    });

    if (success) {
      setIsDeleteModalOpen(false);
      setSelectedPackageId(null);
    }
  };

  return (
    <AdminPageLayout title="Packages">
      <main id="main" className="py-6">
        <Toolbar onCreateClick={() => setIsCreateModalOpen(true)} />
        <section id="packages-grid">
          <InfiniteScroll<Package>
            refreshHandle={refreshHandle}
            fetchItems={fetchPackages}
            renderItem={renderItem}
            itemKey={itemKey}
            layout="grid"
            gridCols={{ default: 2, lg: 3 }}
          />
        </section>
      </main>
      <CreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreatePackage}
        isLoading={isLoading}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeletePackage}
      />
    </AdminPageLayout>
  );
};
